import { api } from '../lib/api';

export interface Post {
  id: string;
  title: string | null;
  content: string;
  imageUrl: string | null;
  imageUrls: string[];
  pdfUrl: string | null;
  pdfName: string | null;
  userId: string | null;
  eventId: string | null;
  expiresAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    profileImageUrl: string | null;
  } | null;
  _count: {
    likes: number;
    comments: number;
  };
  likes?: Array<{ user: { id: string; name: string } }>;
}

export interface FeedResponse {
  posts: Post[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface CreatePostInput {
  title?: string;
  content: string;
  imageUrl?: string;
  imageUrls?: string[];
  image?: File;
  images?: File[];
  pdf?: File;
  eventId?: string;
  expiresMonths?: number;
}

export interface UpdatePostInput {
  title?: string;
  content?: string;
  imageUrl?: string;
}

/**
 * Create a new post
 */
export const createPost = async (input: CreatePostInput): Promise<Post> => {
  const imageFiles = input.images && input.images.length > 0
    ? input.images
    : input.image
      ? [input.image]
      : [];

  // If image file or PDF is provided, use multipart form data
  if (imageFiles.length > 0 || input.pdf) {
    const formData = new FormData();
    if (input.title) {
      formData.append('title', input.title);
    }
    formData.append('content', input.content);
    imageFiles.forEach((file) => {
      formData.append('images', file);
    });
    if (input.pdf) {
      formData.append('pdf', input.pdf);
    }
    if (input.eventId) {
      formData.append('eventId', input.eventId);
    }
    if (input.expiresMonths !== undefined) {
      formData.append('expiresMonths', String(input.expiresMonths));
    }

    const response = await api.post<Post>('/posts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Otherwise use JSON
  const response = await api.post<Post>('/posts', {
    title: input.title,
    content: input.content,
    imageUrl: input.imageUrl,
    imageUrls: input.imageUrls,
    eventId: input.eventId,
    expiresMonths: input.expiresMonths,
  });
  return response.data;
};

/**
 * Get feed of posts
 */
export const getFeed = async (page = 1, limit = 20, eventId?: string | null, archived?: boolean): Promise<FeedResponse> => {
  const response = await api.get<FeedResponse>('/posts', {
    params: { page, limit, eventId, ...(archived ? { archived: true } : {}) },
  });
  return response.data;
};

/**
 * Get a single post by ID
 */
export const getPostById = async (postId: string): Promise<Post> => {
  const response = await api.get<Post>(`/posts/${postId}`);
  return response.data;
};

/**
 * Update a post
 */
export const updatePost = async (
  postId: string,
  input: UpdatePostInput
): Promise<Post> => {
  const response = await api.patch<Post>(`/posts/${postId}`, input);
  return response.data;
};

/**
 * Delete a post
 */
export const deletePost = async (postId: string): Promise<void> => {
  await api.delete(`/posts/${postId}`);
};

/**
 * Get user's posts
 */
export const getUserPosts = async (
  userId: string,
  page = 1,
  limit = 20
): Promise<FeedResponse> => {
  const response = await api.get<FeedResponse>('/posts', {
    params: { page, limit, userId },
  });
  return response.data;
};

/**
 * Get unread posts for the authenticated user
 */
export const getUnreadPosts = async (): Promise<{ posts: Post[]; count: number }> => {
  const response = await api.get<{ posts: Post[]; count: number }>('/posts/unread');
  return response.data;
};

/**
 * Mark a post as viewed
 */
export const markPostAsViewed = async (postId: string): Promise<void> => {
  await api.post('/posts/mark-viewed', { postId });
};

/**
 * Archive or unarchive a post (toggle)
 */
export const archivePost = async (postId: string): Promise<Post> => {
  const response = await api.patch<Post>(`/posts/${postId}/archive`);
  return response.data;
};
